import { useEffect, useRef, useState } from 'react';
import api from '../../../../api';
import { useActionLock } from '../../../../hooks/useActionLock';
import useFlashMessage from '../../../../hooks/useFlashMessage';
const numero = (v) => /^\d+$/.test(String(v ?? '')) && Number.isSafeInteger(Number(v)) && Number(v) <= 2147483647;
const normalizar = (g) => ({ ...g, votosNulos: g.votosNulos ?? '', itens: g.itens.map(i => ({ ...i, votos: i.votos ?? '' })) });
export function calcularResultado(g) {
    if (g.modalidade === 'aclamacao') return { totalValidos: null, totalApurado: null, naoRegistrados: null, completo: g.itens.length === 1, excedeu: false };
    const completo = numero(g.votosNulos) && g.itens.every(i => numero(i.votos)) && g.itens.length > 0;
    const totalValidos = g.itens.reduce((s, i) => s + (numero(i.votos) ? Number(i.votos) : 0), 0);
    const totalApurado = totalValidos + (numero(g.votosNulos) ? Number(g.votosNulos) : 0);
    return { totalValidos, totalApurado, naoRegistrados: g.totalPossiveis - totalApurado, completo, excedeu: totalApurado > g.totalPossiveis };
}
export function useResultadoModule({ eleicao, permissoes, bloqueado, chaveChapas }) {
    const [grupos, setGrupos] = useState([]), [sujos, setSujos] = useState({});
    const [carregando, setCarregando] = useState(false), [erro, setErro] = useState(''), [desatualizado, setDesatualizado] = useState(false);
    const contexto = useRef(0), consulta = useRef(0), pendenteRef = useRef(false), chapasRef = useRef(chaveChapas);
    const { isLocked, run } = useActionLock();
    const { setFlashMessage } = useFlashMessage();
    const pendente = Object.values(sujos).some(Boolean);
    pendenteRef.current = pendente;
    const ocupado = bloqueado || carregando || isLocked;
    const base = `/eleicao/${eleicao.uuid}/resultados`;
    const podeSalvar = (g) => permissoes.autorizado && (g.uuid ? permissoes.editar : permissoes.incluir);
    const carregar = async () => {
        if (!eleicao.uuid || !permissoes.autorizado) return;
        const versao = ++consulta.current, ctx = contexto.current;
        setCarregando(true); setErro('');
        try {
            const { data } = await api.get(base);
            if (!Array.isArray(data) || data.length !== 2) throw new Error('Resposta inválida ao consultar a apuração.');
            if (ctx !== contexto.current || versao !== consulta.current) return;
            setGrupos(data.map(normalizar)); setSujos({}); setDesatualizado(false);
        } catch (e) {
            if (ctx === contexto.current && versao === consulta.current) setErro(e.response?.data?.message || e.message);
        } finally { if (ctx === contexto.current && versao === consulta.current) setCarregando(false); }
    };
    useEffect(() => {
        contexto.current += 1; consulta.current += 1; setGrupos([]); setSujos({}); setErro(''); setDesatualizado(false); setCarregando(false);
        pendenteRef.current = false;
        carregar();
        return () => { contexto.current += 1; consulta.current += 1; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eleicao.uuid, permissoes.autorizado]);
    useEffect(() => {
        if (chapasRef.current === chaveChapas) return;
        chapasRef.current = chaveChapas;
        if (!eleicao.uuid || !permissoes.autorizado) return;
        if (pendenteRef.current) { setDesatualizado(true); return; }
        carregar();
        // A lista de chapas foi recarregada após uma alteração na aba Chapas.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chaveChapas]);
    const alterar = (grupo, campo, value, chapaId) => {
        const g = grupos.find(g => g.grupo === grupo);
        if (!g || ocupado || desatualizado || erro || !podeSalvar(g)) return;
        if (campo === 'modalidade' && value === 'aclamacao' && g.itens.length !== 1) return;
        setGrupos(prev => prev.map(item => {
            if (item.grupo !== grupo) return item;
            if (campo === 'modalidade') return { ...item, modalidade: value, votosNulos: '', itens: item.itens.map(i => ({ ...i, votos: '' })) };
            if (campo === 'votos') return { ...item, itens: item.itens.map(i => i.chapaId === chapaId ? { ...i, votos: value } : i) };
            return { ...item, [campo]: value };
        }));
        setSujos(prev => ({ ...prev, [grupo]: true }));
    };
    const salvar = (grupo) => {
        const g = grupos.find(g => g.grupo === grupo);
        if (!g || ocupado || erro || desatualizado || !podeSalvar(g)) return;
        const total = calcularResultado(g);
        if (!total.completo || total.excedeu) { setFlashMessage('Preencha os votos de todas as chapas e os nulos, sem ultrapassar o total possível. Aclamação exige chapa única.', 'warning'); return; }
        const ctx = contexto.current;
        return run(async () => {
            try {
                const { data } = await api.post(`${base}/${grupo}`, { versao: g.versao, totalPossiveis: g.totalPossiveis,
                    modalidade: g.modalidade, votosNulos: g.modalidade === 'aclamacao' ? null : Number(g.votosNulos),
                    itens: g.itens.map(i => ({ chapaId: i.chapaId, votos: g.modalidade === 'aclamacao' ? null : Number(i.votos) })) });
                if (ctx !== contexto.current) return;
                if (data.type !== 'success' || !data.resultado) throw new Error(data.message || 'Apuração não salva.');
                setGrupos(prev => prev.map(item => item.grupo === grupo ? normalizar(data.resultado) : item));
                setSujos(prev => ({ ...prev, [grupo]: false })); setFlashMessage(data.message, 'success');
            } catch(e) {
                if (ctx !== contexto.current) return;
                if (e.response?.status === 409) setDesatualizado(true);
                setFlashMessage(e.response?.data?.message || e.message, 'error');
            }
        });
    };
    const recarregar = () => {
        if (ocupado) return;
        if (pendente && !window.confirm('Descartar os votos ainda não salvos e recarregar a apuração dos dois grupos?')) return;
        return carregar();
    };
    return { grupos, sujos, pendente, carregando, ocupado, erro, desatualizado, podeSalvar, alterar, salvar, recarregar };
}
